<template>
  <v-form
    ref="form"
    v-model="valid"
    @submit.prevent="submit"
    :lazy-validation="true"
    :disabled="isSaving"
  >
    <v-card>
      <v-card-title>
        <span class="headline">{{ titlePrefix }} Product</span>
        <v-spacer />
        <v-btn v-if="ownProduct.id" icon text small @click="$dialog({message: `Operator Id:\n${operator.id}\n\nProduct Id:\n${ownProduct.id}`})">
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <image-input
          v-if="itemsLayout.images"
          label="Image"
          v-model="image"
          :crop-width="480"
          :crop-height="480"
          :crop-clamp="true"
          :quality="0.7"
          ouput-type="jpeg"
          :disabled="isSaving"
        ></image-input>

        <v-text-field name="productname" v-model="ownProduct.name" label="Name" :rules="stringValidation"></v-text-field>

        <v-textarea name="description" v-model="ownProduct.description" label="Description" :auto-grow="true" rows="1" :rules="stringValidation"></v-textarea>

        <v-text-field name="price" v-model="ownProduct.price" label="Price" suffix=":-" type="tel" :rules="priceValidation"></v-text-field>

        <v-select :items="itemGroupsOptions" v-model="ownProduct.itemgroup" label="Item group" :rules="stringValidation"></v-select>

        <v-row>
          <v-col cols="6">
            <v-text-field
              name="account number"
              label="Account number"
              v-model="ownProduct.account_number"
              type="tel"
              :rules="accountValidation"
              :placeholder="!!selectedItemGroup && !!selectedItemGroup.account_number ? selectedItemGroup.account_number : ''"
              :persistent-placeholder="!!selectedItemGroup && !!selectedItemGroup.account_number"
            />
          </v-col>

          <v-col cols="6">
            <v-text-field
              name="cost center"
              label="Cost Center"
              v-model="ownProduct.cost_center"
              type="tel"
              :placeholder="!!selectedItemGroup && !!selectedItemGroup.cost_center ? selectedItemGroup.cost_center : ''"
              :persistent-placeholder="!!selectedItemGroup && !!selectedItemGroup.cost_center"
            />
          </v-col>
        </v-row>

        <v-row class="no-gutters">
          <v-col cols="6">
            <v-text-field
              name="stock balance"
              label="Stock Balance"
              v-model="ownProduct.stock_balance"
              clearable
              type="number"
              step="any"
              min="0"
              ref="input"
              :rules="stockBalanceValidation"
            />
          </v-col>
        </v-row>

        <v-select v-if="operator.product_categories" :items="categories" v-model="ownProduct.category" label="Category" item-text="title" item-value="id"></v-select>

        <v-checkbox
          v-model="ownProduct.has_sideorders"
          @change="updateValidation"
          label="Product has side orders"
        ></v-checkbox>
        <v-expand-transition>
          <v-sheet
            v-show="ownProduct.has_sideorders"
            elevation="0"
            tile
          >
            <side-orders-select
              v-model="ownProduct.sideorders"
              :sideOrders="sideOrders"
              :rules="sideOrderValidation"
            ></side-orders-select>
          </v-sheet>
        </v-expand-transition>

        <v-row>
          <v-col cols="6">
            <v-checkbox
              v-model="ownProduct.hidden"
              label="Hidden"
              messages="Hidden in the app and can't be bought"
            ></v-checkbox>
          </v-col>

          <v-col cols="6">
            <v-checkbox
              v-model="ownProduct.unlisted"
              label="Unlisted"
              messages="Not shown in the app but can be bought"
            ></v-checkbox>
          </v-col>
        </v-row>

      </v-card-text>

      <v-card-actions>
        <v-btn depressed :disabled="!valid" :loading="isSaving" color="primary" type="submit">Save</v-btn>
        <v-btn text @click="cancel" color="primary">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import {ImageInput} from 'dync-imageinput';
import {getItemById} from '@dynapp/wc-api-generic';
import SideOrdersSelect from './SideOrdersSelect.vue';
import itemsLayouts from './itemsLayouts';

function isNumeric(n) {
  return /^[0-9]+$/g.test(n);
}

export default {
  props: {
    operator: {
      type: Object
    },
    product: {
      type: Object
    },
    itemGroups: {
      type: Array
    },
    sideOrders: {
      type: Array
    },
    categories: {
      type: Array
    }
  },
  data () {
    return {
      valid: true,
      stringValidation: [v => (v != null && v !== '') || 'Cannot be empty'],
      accountValidation: [v => !v || /^\d*$/.test(v) || 'Only numbers'],
      priceValidation: [
        v => v != null || 'Must be a number',
        v => isNumeric(v) || 'Must be a whole number',
        v => v >= 0 || 'Can\'t be negative'
      ],
      sideOrderValidation: [this._sideOrderValidation],
      stockBalanceValidation: [v => !v || /^\d*$/.test(v) || 'Only numbers'],
      ownProduct: {},
      imageName: 'dync-appshop-product-image.jpg',
      image: null,
      hadImage: false
    }
  },
  computed: {
    itemsLayout() {
      var layout = getItemById(itemsLayouts, this.operator.layout_items, 'value');
      if (layout == null) {
        layout = getItemById(itemsLayouts, '2-grid', 'value');
      }
      return layout || {};
    },
    isSaving () {
      return this.$store.getters.isLoadingFor(['products.create', 'products.update']);
    },
    titlePrefix () {
      return this.ownProduct.id ? 'Edit' : 'Add';
    },
    itemGroupsOptions () {
      var options = this.itemGroups.slice(0);
      options = options.map((item) => {
        return {
          value: item.id,
          text: item.name +'  (Vat '+ item.vat + '%)'
        }
      });
      options.sort((a, b) => {
        if (a.text > b.text) {
          return 1;
        } else if (a.text < b.text) {
          return -1;
        } else {
          return 0
        }
      });
      return options;
    },
    selectedItemGroup () {
      if (this.ownProduct && this.ownProduct.itemgroup) {
        return this.getItemGroupById(this.ownProduct.itemgroup);
      }
      return null;
    }
  },
  watch: {
    product (newVal) {
      this.setOwnProduct(newVal);
    }
  },
  methods: {
    setOwnProduct (product) {
      this.image = null;
      if (product) {
        this.ownProduct = Object.assign({
          sideorders: []
        }, product);
        this.hadImage = false;
        if (product.id && this.ownProduct.attachment_names && this.ownProduct.attachment_names.indexOf(this.imageName) != -1) {
          this.hadImage = true;
          this.image = this.$store.getters['products/getAttachmentUrl'](product.id, this.imageName);
        }
      } else {
        this.ownProduct = {};
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    submit () {
      if (!this.$refs.form.validate())
        return;

      var item = Object.assign({}, this.ownProduct);

      var old_stock_balance = this.product.stock_balance;
      var new_stock_balance = item.stock_balance || '';
      delete item.stock_balance;
      if (old_stock_balance != new_stock_balance) {
        if (new_stock_balance == '') {
          item.stock_balance_diff = 'empty';
        } else {
          item.stock_balance_diff = parseInt(new_stock_balance) - parseInt(old_stock_balance || 0);
        }
      }

      if (this.image instanceof Blob)
        item.attachments = [{
          name: this.imageName,
          file: this.image
        }];
      if (!this.image && this.hadImage)
        item.attachments_remove = [this.imageName];

      this.$emit('submit', item);
    },
    cancel () {
      this.$emit('cancel')
    },
    onImage (image) {
      this.image = image;
    },
    reset () {
      this.image = null;
      this.setOwnProduct(null);
    },
    _sideOrderValidation (v) {
      if (!this.ownProduct.has_sideorders) {
        return true;
      }
      return (v != null && v !== '' && v.length !== 0) || 'Cannot be empty';
    },
    updateValidation () {
      if (this.valid == false) {
        this.$refs.form.validate();
      }
    },
    getItemGroupById (id) {
      return getItemById(this.itemGroups, id);
    }
  },
  mounted () {
    if (this.product)
      this.setOwnProduct(this.product);
  },
  components: {
    ImageInput,
    SideOrdersSelect
  }
}
</script>

<style lang="scss" scoped>
  .product-form__image-uploader {
    height: 300px;
    width: 300px;
    margin: auto;
  }
</style>
